import { createFileRoute, redirect } from '@tanstack/react-router'
import { ErrorComponent } from '@/components/ErrorComponent'
import { EntityDetailPageView } from '@/components/entity-page-views'
import { LoadingComponent } from '@/components/LoadingComponent'
import {
	getEntityPageBreadcrumb,
	getEntityPageOverride,
	getEntityTabs,
	loadEntityTabRoute,
	resolveEntity,
	validateEntityTabSearchOptional,
} from '@/entity-config-runtime'
import {
	canReadEntityOrTab,
	firstReadableTab,
} from '@/integrations/casl/route-ability'

export const Route = createFileRoute(
	'/_authenticated/$entity/$entityId/details',
)({
	beforeLoad: ({ params, context }) => {
		const entity = String(params.entity ?? '')
		const entityId = String(params.entityId ?? '')
		if (!canReadEntityOrTab(context.ability, entity, 'details')) {
			const Entity = resolveEntity(entity)
			const fallbackTab = firstReadableTab(
				context.ability,
				entity,
				getEntityTabs(Entity).map((item) => item.key),
			)

			if (fallbackTab) {
				throw redirect({
					to: '/$entity/$entityId/$tab',
					params: {
						entity,
						entityId,
						tab: fallbackTab,
					},
					replace: true,
				})
			}

			throw new Error(
				`Missing read permission for tab "details" on entity "${entity}"`,
			)
		}
	},
	validateSearch: validateEntityTabSearchOptional,
	component: EntityDetailRoute,
	staticData: {
		breadcrumb: ({ params, search }) =>
			getEntityPageBreadcrumb(String(params.entity ?? ''), 'detail', {
				params,
				search,
			}),
	},
	loaderDeps({ search }) {
		return search
	},
	loader({ params, deps, context: { queryClient } }) {
		return loadEntityTabRoute({
			params: { ...params, tab: 'overview' },
			search: deps,
			context: { queryClient },
		})
	},
	pendingComponent: LoadingComponent,
	errorComponent: ErrorComponent,
})

function EntityDetailRoute() {
	const { entity, entityId } = Route.useParams()
	const basePageProps = {
		entity,
		entityId,
	}
	const OverrideComponent = getEntityPageOverride(
		entity,
		'detail',
	)?.detailComponent

	if (OverrideComponent) {
		return <OverrideComponent {...basePageProps} />
	}

	return <EntityDetailPageView {...basePageProps} />
}
